import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { Spinner } from "@/components/ui/Loader/Spinner";
import { cn } from "@/utils/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-base font-medium ring-offset-white focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:ring-offset-slate-950 dark:focus-visible:ring-slate-300 w-max shrink-0 cursor-pointer border border-transparent px-6 py-3 font-archivo active:scale-[98%] disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        default: "bg-whiteSmoke text-black",
        outline: "border-nightRider text-nightRider hover:border-edge-1",
        secondary:
          "bg-surface-dull text-nightRider hover:bg-surface-dull-hover active:bg-surface-dull-hover",
        ghost:
          "bg-transparent text-nightRider hover:bg-paleGrey/50 active:bg-paleGrey/70",
        link: "text-slate-900 underline-offset-4 underline hover:underline dark:text-slate-50",
        dark: "bg-nightRider text-white hover:bg-nightRider/90 active:bg-black disabled:bg-nightRider/90",
        accent:
          "bg-surface-accent text-white hover:bg-surface-accent-hover active:bg-surface-accent-hover",
        "accent-invert": "bg-accent-invert text-type-accent",
        "accent-light":
          "bg-accent-muted text-type-accent hover:bg-accent-soft active:bg-accent-muted disabled:bg-accent-tint disabled:text-type-accent",
        confirm:
          "bg-surface-confirm text-white hover:bg-surface-confirm-hover active:bg-surface-confirm-hover",
        confirmSoft:
          "bg-surface-confirm-soft text-white hover:bg-surface-confirm-soft active:bg-surface-confirm-soft disabled:bg-surface-confirm-soft disabled:opacity-100",
        negative:
          "bg-surface-error text-white hover:bg-surface-error-hover active:bg-surface-error-hover disabled:bg-surface-error-disabled disabled:text-black",
        negativeSoft:
          "bg-surface-error-soft text-type-error hover:bg-surface-error-soft active:bg-surface-error-soft disabled:bg-surface-error-soft disabled:text-type-error disabled:opacity-100",
        orange:
          "bg-surface-orange text-white hover:bg-surface-orange-hover active:bg-surface-orange-hover disabled:bg-surface-dull disabled:text-black",
        green:
          "bg-azaTeal text-[#71B4A7] hover:bg-azaTeal/90 active:bg-azaTeal/70 disabled:bg-[#D1E0FF] disabled:text-[#84ADFF]",
        white:
          "border border-black bg-transparent text-black hover:bg-gray-100 disabled:bg-[#D1E0FF] disabled:text-[#84ADFF] dark:border-white dark:text-white dark:hover:bg-transparent",
        dull: "bg-gainsboro text-black font-archivo font-semibold tracking-wide  disabled:bg-gainsboro disabled:text-black",
        "inline-danger":
          "h-auto! rounded-none! border-0! bg-transparent! p-0! text-sm leading-5 text-edge-error",
        "inline-accent":
          "h-auto! rounded-none! border-0! bg-transparent! p-0! text-sm font-normal leading-5 text-type-accent",
        "inline-base":
          "h-auto! rounded-none! border-0! bg-transparent! p-0! text-sm leading-5 text-accent-blue",
        "inline-base-underline":
          "h-auto! rounded-none! border-0! bg-transparent! p-0! text-sm leading-5 text-accent-blue underline",
        "inline-muted":
          "h-auto! rounded-none! border-0! bg-transparent! p-0! text-sm leading-5 text-type-2 underline",
      },
      size: {
        default: "h-10 px-6 py-3",
        sm: "h-8 rounded-md px-4 text-sm leading-[20px]",
        lg: "h-14 rounded-md px-8",
        icon: "h-10 w-10 p-0",
        "icon-sm": "h-8 w-8 p-2",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Disables the button and shows a Spinner over its content. Ignored with `asChild`. */
  isLoading?: boolean;
  /** Accessible name while loading; defaults to the button's own content */
  loadingLabel?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading = false,
      loadingLabel,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    if (asChild) {
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          // Spread, as Slot's types omit `disabled`; it still reaches the child
          {...{ ...props, disabled }}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        className={cn(
          buttonVariants({ variant, size, className }),
          isLoading && "relative",
        )}
        ref={ref}
        disabled={disabled || isLoading}
        aria-busy={isLoading || undefined}
        {...props}
      >
        {isLoading ? (
          <>
            {/* Keeps the button's width while the content is hidden */}
            <span className="invisible contents">{children}</span>
            <Spinner className="absolute inset-0 m-auto" />
            {/* The hidden content drops out of the accessible name */}
            <span className="sr-only">{loadingLabel ?? children}</span>
          </>
        ) : (
          children
        )}
      </button>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
